import type {
	IDexfSettings,
	IDexfSettingsKompisEndpoints,
	IDexfSettingsKompisLocalization,
	IDexfSettingsKompisPriceSettings,
	IDexfSettingsKompisServiceSettings,
	IDexfSettingsKompisUrls,
	IDexfSettingsLocalisation
} from '@inter-ikea-kompis/types';
import { LocaleUtility } from '@inter-ikea-kompis/utilities';

import type IDexfSettingsResponse from './types/IDexfSettingsResponse.js';

/**
 * This class is used for transforming data from DEXF Settings.
 */
export default class DexfSettingsTransformer {
	private readonly locale: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.locale Locale.
	 */
	constructor(options: { locale: string }) {
		this.locale = options.locale;
	}

	/**
	 * Transforms data into a DexfSettings model.
	 *
	 * @param data Response data to parse.
	 * @returns IDexfSettings model.
	 */
	public transform(data: IDexfSettingsResponse): IDexfSettings {
		const storeId = data.kompis?.localization?.storeId ? data.kompis.localization.storeId : null;
		const langId = data.kompis?.localization?.langId ? data.kompis.localization.langId : null;

		const dexfSettings: IDexfSettings = {
			kompis: {
				endpoints: this.getEndpoints(data.kompis?.endpoints || <IDexfSettingsKompisEndpoints>{}),
				urls: this.getUrls(data.kompis?.urls || {}, storeId, langId),
				serviceSettings: <IDexfSettingsKompisServiceSettings>(data.kompis?.serviceSettings || {}),
				localization: <IDexfSettingsKompisLocalization>(data.kompis?.localization || {}),
				priceSettings: <IDexfSettingsKompisPriceSettings>(data.kompis?.priceSettings || {})
			},
			localisation: <IDexfSettingsLocalisation>(data.localisation || {})
		};

		// Include all custom settings which the teams could have
		for (const key of Object.keys(data)) {
			if (!dexfSettings[key]) {
				dexfSettings[key] = data[key];
			}
		}

		return dexfSettings;
	}

	/**
	 * Parses URL:s.
	 *
	 * @param urls URL:s.
	 * @param storeId Store id.
	 * @param langId Language id.
	 * @returns Parsed URL:s.
	 */
	private getUrls(
		urls: object,
		storeId: string | null,
		langId: string | null
	): IDexfSettingsKompisUrls {
		const { country, language } = LocaleUtility.getCountryAndLanguage(this.locale);
		const newUrls = {};
		for (const key of Object.keys(urls)) {
			newUrls[key] = urls[key]
				.replace('{country}', country)
				.replace('{language}', language)
				.replace('{storeId}', storeId)
				.replace('{langId}', langId);
		}
		return <IDexfSettingsKompisUrls>newUrls;
	}

	/**
	 * Parses endpoints.
	 *
	 * @param endpoints Endpoints.
	 * @returns Parsed endpoints.
	 */
	private getEndpoints(endpoints: IDexfSettingsKompisEndpoints): IDexfSettingsKompisEndpoints {
		const { country, language } = LocaleUtility.getCountryAndLanguage(this.locale);
		const newEndpoints = {};
		for (const key of Object.keys(endpoints)) {
			newEndpoints[key] = endpoints[key]
				.replace('{country}', country)
				.replace('{language}', language)
				.replace('{retailUnit}', country?.toUpperCase())
				.replace('{locale}', this.locale);
		}
		return <IDexfSettingsKompisEndpoints>newEndpoints;
	}
}
